import {ConnectedComponent} from 'lowkey-component';

import {Backdrop} from '@components/Backdrop';
import {TextAnimation} from '@components/helpers/TextAnimation';
import {sleep} from '@lib/utils';
import {createApplicationState} from '@services/ApplicationState';
import {MediaQuery} from '@services/MediaQuery';
import {ScrollService} from '@services/ScrollService';
import {appSelectors} from '@store/appSlice';

export class KvVideo extends ConnectedComponent {
  mq = MediaQuery.create();
  observer: IntersectionObserver | null = null;
  color = '';
  video: HTMLVideoElement | undefined = undefined;
  inner: HTMLDivElement | undefined | null = undefined;
  initialTop = 0;
  fullscreen: HTMLAnchorElement | undefined = undefined;
  sound: HTMLAnchorElement | undefined = undefined;
  fullscreenContainer: HTMLDivElement | undefined = undefined;
  container: HTMLDivElement | undefined = undefined;
  copy: HTMLDivElement | undefined = undefined;
  subline: HTMLDivElement | undefined = undefined;
  scroll = ScrollService.create();
  state = createApplicationState();

  constructor(element: HTMLDivElement) {
    super(element);
    this.onIntersection = this.onIntersection.bind(this);
    this.onPressFullscreen = this.onPressFullscreen.bind(this);
    this.onPressSound = this.onPressSound.bind(this);
  }

  didMount() {
    this.inner =
      this.element?.querySelector<HTMLDivElement>('.o-kv-video_inner');
    this.video = this.element?.querySelector(
      '.o-kv-video_object',
    ) as HTMLVideoElement;
    this.fullscreen = this.element?.querySelector(
      '.o-kv-video_fullscreen',
    ) as HTMLAnchorElement;
    this.sound = this.element?.querySelector(
      '.o-kv-video_sound',
    ) as HTMLAnchorElement;
    this.fullscreenContainer = this.element?.querySelector(
      '.o-kv-video_fullscreenContainer',
    ) as HTMLDivElement;
    this.container = this.element?.querySelector(
      '.o-kv-video_container',
    ) as HTMLDivElement;
    this.copy = this.element.querySelector(
      '.o-kv-video_copy .js-text',
    ) as HTMLDivElement;
    this.subline = this.element.querySelector(
      '.o-kv-video_subline .js-text',
    ) as HTMLDivElement;

    const list = this.video;
    const {top} = list.getBoundingClientRect();
    this.initialTop = top + window.scrollY;

    if (this.inner) {
      this.observer = new IntersectionObserver(this.onIntersection, {
        rootMargin: '0px',
        threshold: [0.1],
      });
      this.observer.observe(this.inner);
    }

    this.fullscreen.addEventListener('click', this.onPressFullscreen);
    this.sound.addEventListener('click', this.onPressSound);

    if (this.mq.isDesktop()) {
      const backdrop = new Backdrop(this.element as HTMLDivElement);
      backdrop.mount();
      const copyAnimation = new TextAnimation(this.copy);
      const sublineAnimation = new TextAnimation(this.subline);
      copyAnimation.hide();
      sublineAnimation.hide();

      this.observe(appSelectors.selectOneViewStatus, async (status) => {
        if (status === 'down') {
          await sleep(300);
          copyAnimation.show();
          sublineAnimation.show();
          backdrop.process(this.element);
        }
        if (status === 'up') {
          copyAnimation.hide();
          sublineAnimation.hide();
        }
      });
    }
  }

  didUnmount() {
    this.observer?.disconnect();
    this.fullscreen?.removeEventListener('click', this.onPressFullscreen);
    this.sound?.removeEventListener('click', this.onPressSound);
  }

  async onPressFullscreen() {
    this.fullscreenContainer?.classList.toggle('fullscreen');
    if (this.fullscreenContainer?.classList.contains('fullscreen')) {
      this.state.updateVideoIsFullScreen(true);
      const top = this.scroll.stopScroll(document.body);
      this.fullscreenContainer.style.top = `${top}px`;
    } else {
      this.state.updateVideoIsFullScreen(false);
      this.scroll.startScroll();
      if (this.fullscreenContainer) {
        this.fullscreenContainer.style.top = `${0}px`;
      }
    }
  }

  onPressSound() {
    if (this.video) {
      if (this.video?.muted) {
        this.video.muted = false;
        this.sound?.classList.remove('off');
      } else {
        this.video.muted = true;
        this.sound?.classList.add('off');
      }
    }
  }

  onIntersection(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      // if (!entry.rootBounds) return
      // const isDisabled = entry.intersectionRatio < 1
      const isDisabled = true;
      if (isDisabled && !entry.isIntersecting) {
        this.disable();
      } else {
        this.enable();
      }
    }
  }

  enable() {
    this.video?.play();
  }

  disable() {
    if (!this.fullscreenContainer?.classList.contains('fullscreen')) {
      this.video?.pause();
    }
  }
}
